/**
 * S4-T06 — Shared route-history store for the Recents drawer.
 *
 * Distinct from CommandPalette's `ga-erp-cmdk-recents`:
 *   - That store records CMDk-resolved item IDs (page:/x, project:p-01) —
 *     answers "what did I jump to with ⌘K?".
 *   - This store records actual navigated routes with timestamps —
 *     answers "where have I been?". Covers every navigation, not just
 *     ⌘K-driven ones.
 */

const RECENTS_KEY = 'ga-erp-route-recents';
const RECENTS_LIMIT = 20;

/** Routes that should never be recorded — too noisy or not useful. */
const RECENTS_SKIP = new Set<string>(['/', '/landing']);

export interface RouteRecent {
  path: string;
  /** ISO timestamp of the last visit. Re-visits update in place, not duplicate. */
  visitedAt: string;
}

export function readRouteRecents(): RouteRecent[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RouteRecent[]) : [];
  } catch {
    return [];
  }
}

function writeRouteRecents(entries: RouteRecent[]): void {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(entries.slice(0, RECENTS_LIMIT)));
  } catch {
    /* ignore quota / disabled storage */
  }
}

/**
 * Records a route visit. De-duplicates by path: re-visiting an existing entry
 * lifts it to the top with a refreshed timestamp.
 */
export function recordRouteVisit(path: string): RouteRecent[] {
  if (RECENTS_SKIP.has(path)) return readRouteRecents();
  const now = new Date().toISOString();
  const prev = readRouteRecents();
  const next: RouteRecent[] = [
    { path, visitedAt: now },
    ...prev.filter((r) => r.path !== path),
  ].slice(0, RECENTS_LIMIT);
  writeRouteRecents(next);
  return next;
}

export function clearRouteRecents(): void {
  try {
    localStorage.removeItem(RECENTS_KEY);
  } catch {
    /* ignore */
  }
}
